/**
 * Parse a free-text duration string into minutes.
 *
 * Athletes reach for many formats depending on device / habit:
 *   - "90"        → 90 minutes (bare number = minutes)
 *   - "90m"       → 90 minutes
 *   - "90 mins"   → 90 minutes
 *   - "1:30"      → 90 minutes (hh:mm)
 *   - "1:30:00"   → 90 minutes (hh:mm:ss — seconds ignored to nearest min)
 *   - "1h30"      → 90 minutes
 *   - "1h 30m"    → 90 minutes
 *   - "1.5"       → 90 minutes when a colon or 'h' is present, else assumed
 *                   as minutes when < 60 and hours when >= 60. Actually: bare
 *                   decimal is ambiguous — we treat < 10 as HOURS (1.5 = 90 min,
 *                   2 = 120 min) and >= 10 as MINUTES (45 = 45 min, 90 = 90 min).
 *   - "1.5h"      → 90 minutes
 *   - "1.5 hrs"   → 90 minutes
 *   - "1 hour"    → 60 minutes
 *   - ""          → null
 *
 * Returns a number (float, minutes) or null when the input is empty /
 * unparseable. Never throws.
 */
export function parseDurationToMinutes(input) {
  if (input === null || input === undefined) return null;
  const str = String(input).trim().toLowerCase();
  if (str === "") return null;

  // hh:mm[:ss] form — clearest signal, check first.
  const colonMatch = str.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (colonMatch) {
    const h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    const s = colonMatch[3] ? parseInt(colonMatch[3], 10) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m + s / 60;
  }

  // "1h30" — hour followed immediately by 1–2 digits with no explicit
  // 'm' unit. Check BEFORE the general keyword scan so "1h30" doesn't
  // silently become 1h + 0min = 60.
  const shortHmMatch = str.match(/^(\d+)h(\d{1,2})$/);
  if (shortHmMatch) {
    const h = parseInt(shortHmMatch[1], 10);
    const m = parseInt(shortHmMatch[2], 10);
    return h * 60 + m;
  }

  // Explicit hours + minutes: "1h30m", "1h 30m", "2h", "1 hour 15 min", etc.
  // Grab any number followed by an h/hr/hrs/hour keyword, then any number
  // followed by an m/min/mins/minute keyword. Either is optional.
  const hoursMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)(?![a-z])/);
  const minsMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)(?![a-z])/);
  if (hoursMatch || minsMatch) {
    const h = hoursMatch ? parseFloat(hoursMatch[1]) : 0;
    const m = minsMatch ? parseFloat(minsMatch[1]) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  }

  // Bare number.
  const bare = parseFloat(str);
  if (!Number.isFinite(bare)) return null;
  // Ambiguity: is "1.5" hours or minutes? Convention: a decimal below 10
  // is hours (1.5 = 90 min), 10+ is minutes (45, 90, 120…). A whole
  // number is always minutes (45, 90, 120). Rationale: nobody writes "5"
  // meaning "5 hours" — they'd type "5h" — but "1.5" meaning "1.5 hours"
  // is idiomatic.
  const isDecimal = /\./.test(str);
  if (isDecimal && bare < 10) {
    return bare * 60;
  }
  return bare;
}


/**
 * Format a minutes number back to a human hh:mm string ("1:30") for
 * display when re-showing a saved value. Useful for placeholders and
 * inline hints — the athlete's stored value is a bare number but we
 * want to remind them what "90" translates to.
 */
export function formatMinutesAsHhMm(min) {
  if (min == null || !Number.isFinite(Number(min))) return "";
  const n = Number(min);
  if (n < 60) return `${Math.round(n)}m`;
  const h = Math.floor(n / 60);
  const rem = Math.round(n - h * 60);
  if (rem === 0) return `${h}h`;
  return `${h}h${String(rem).padStart(2, "0")}m`;
}
